/**
 * Authenticated fetch wrapper.
 * Acquires an Azure AD access token silently (or via redirect) and attaches
 * it as a Bearer token to every API request.
 */

import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig, tokenRequest } from '../authConfig';

// Singleton MSAL instance — shared with index.js MsalProvider
let msalInstance = null;

/**
 * Set the MSAL instance (called from index.js after creation).
 */
export function setMsalInstance(instance) {
  msalInstance = instance;
}

/**
 * Get the current MSAL instance.
 */
export function getMsalInstance() {
  return msalInstance;
}

/**
 * Acquire an access token for the API silently, falling back to redirect.
 * Returns the access token string.
 */
async function getAccessToken() {
  if (!msalInstance) {
    throw new Error('MSAL instance not initialized. Call setMsalInstance() first.');
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No authenticated user. Please sign in.');
  }

  const request = {
    ...tokenRequest,
    account: accounts[0],
  };

  try {
    const response = await msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Token expired or consent required — use popup
      const popupResponse = await msalInstance.acquireTokenPopup(request);
      return popupResponse.accessToken;
    }
    throw error;
  }
}

/**
 * Drop-in replacement for window.fetch that adds the Authorization header.
 * Usage: import { authFetch } from './services/authFetch';
 *        const response = await authFetch(url, options);
 */
export async function authFetch(url, options = {}) {
  const token = await getAccessToken();

  const headers = {
    ...options.headers,
    Authorization: `Bearer ${token}`,
  };

  return fetch(url, {
    ...options,
    headers,
  });
}

export default authFetch;
